import { MockCard, MockButton, MockWarning } from '../../components/mockups/MockUI'
import { MockTable } from '../../components/mockups/MockTable'
import { MockBadge } from '../../components/mockups/MockBadge'

export function OrdersOverview() {
  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-2">
          Раздел «Заказы»
        </h2>
        <p className="text-sm text-gray-600 dark:text-gray-400">
          Заказ — продолжение заявки после того, как клиент согласился на КП. Один заказ = одна
          заявка. В заказе живут счета, оплаты, документы и история статусов. Счёт CRM не выставляет
          — его формирует 1С:УНФ, мы готовим Excel-шаблон для загрузки в УНФ.
        </p>
      </div>
      <MockCard>
        <div className="text-sm text-gray-600 dark:text-gray-400 space-y-3">
          <div className="font-medium text-gray-800 dark:text-gray-200">Путь «заявка → заказ»:</div>
          <ol className="list-decimal list-inside space-y-1">
            <li>Позиции заявки, которые клиент берёт, переводятся в статус «Счёт»</li>
            <li>
              В карточке заявки нажмите{' '}
              <MockButton variant="secondary">Выгрузить шаблон счёта (УНФ)</MockButton>
            </li>
            <li>
              В модальном окне укажите условие оплаты, предоплату, НДС и юрлицо-продавца — заказ
              создастся (или пополнится) по позициям в статусе «Счёт», Excel-шаблон скачается
            </li>
            <li>Откроется карточка заказа — дальше работа идёт в разделе «Заказы»</li>
          </ol>
        </div>
      </MockCard>
      <MockTable
        caption="Статусы заказа"
        columns={[{ header: 'Статус' }, { header: 'Что означает' }]}
        rows={[
          [
            <MockBadge color="gray">Новый</MockBadge>,
            'Транзитный — при создании заказ сразу уходит дальше',
          ],
          [
            <MockBadge color="yellow">Ждёт согласования РОП</MockBadge>,
            'Плановая предоплата меньше 50% — нужно решение РОП',
          ],
          [
            <MockBadge color="blue">Подтверждён</MockBadge>,
            'Согласование не требовалось или получено',
          ],
          [<MockBadge color="purple">Ждём оплату</MockBadge>, 'Счёт у клиента, ждём деньги'],
          [
            <MockBadge color="teal">Частично оплачен</MockBadge>,
            'Оплачена часть суммы — ставится автоматически по платежам',
          ],
          [
            <MockBadge color="green">Оплачен</MockBadge>,
            'Оплачена вся сумма по счетам — ставится автоматически',
          ],
          [<MockBadge color="green">Закрыт</MockBadge>, 'Заказ выполнен и закрыт'],
          [<MockBadge color="red">Отказ</MockBadge>, 'Клиент отказался — с причиной'],
          [<MockBadge color="gray">Архив</MockBadge>, 'Сквозной — попасть можно из любого статуса'],
        ]}
      />
      <MockCard>
        <div className="text-sm text-gray-600 dark:text-gray-400 space-y-2">
          <p>
            Статусы «Частично оплачен» и «Оплачен» руками не ставятся — система пересчитывает их
            сама по сумме зафиксированных платежей. Ручные кнопки в карточке заказа:{' '}
            <MockButton variant="secondary">Ждём оплату</MockButton>,{' '}
            <MockButton variant="secondary">Закрыть заказ</MockButton>,{' '}
            <MockButton variant="danger">Отказ</MockButton> (с обязательной причиной) и «В архив»
            (доступна из «Закрыт» и «Отказ»).
          </p>
          <p>
            Список заказов — таблица с колонками «Заявка», «Клиент», «Менеджер», «Статус», «Сумма
            (КП)», «По счетам», «Оплачено», «Осталось», «Счетов», «Создан». Над таблицей — виджет
            «Воронка продаж» (количество и сумма по каждому статусу), фильтр по статусу и поиск
            «Поиск: № заявки / клиент / № заказа».
          </p>
          <p>
            В карточке заказа — сводка «Сумма по счетам / Оплачено / Осталось / Условие оплаты» и
            таблица «Позиции заказа». Колонка «Сопровод» показывает, кто закупает позицию: закупщик
            (Китай/Производители) или менеджер (Склад/РФ/Стоки); выделив строки чекбоксами, можно
            назначить сопровода вручную или вернуть «Авто».
          </p>
          <p className="font-medium text-gray-800 dark:text-gray-200">
            Источник истины по деньгам — сумма выставленных счетов, а не сумма КП.
          </p>
        </div>
      </MockCard>
    </div>
  )
}

export function OrdersInvoicesPayments() {
  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-2">
          Счета и оплаты
        </h2>
        <p className="text-sm text-gray-600 dark:text-gray-400">
          По одному заказу может быть несколько счетов (сценарий «два КП → два счёта»). Каждый счёт
          — это подготовленный Excel-шаблон для 1С:УНФ плюс метаданные; печатную форму счёта делает
          сама УНФ.
        </p>
      </div>
      <MockTable
        caption="Статусы счёта"
        columns={[{ header: 'Статус' }, { header: 'Что означает' }]}
        rows={[
          [<MockBadge color="gray">Черновик</MockBadge>, 'Шаблон создан, но ещё не зафиксирован'],
          [<MockBadge color="blue">Выставлен</MockBadge>, 'Шаблон выгружен — счёт «выставлен»'],
          [<MockBadge color="green">Оплачен</MockBadge>, 'По счёту прошла полная оплата'],
          [<MockBadge color="red">Аннулирован</MockBadge>, 'Счёт отменён'],
        ]}
      />
      <MockCard>
        <div className="text-sm text-gray-600 dark:text-gray-400 space-y-3">
          <div className="font-medium text-gray-800 dark:text-gray-200">Блок «Счета»:</div>
          <ul className="list-disc list-inside space-y-1">
            <li>Клик по строке счёта открывает его позиции — цену и количество можно править</li>
            <li>
              Кнопка <MockButton variant="secondary">№ из УНФ</MockButton> — вписать номер счёта,
              который присвоила УНФ
            </li>
            <li>
              <MockButton variant="secondary">Скачать шаблон</MockButton> — Excel по этому счёту
            </li>
            <li>
              <MockButton variant="secondary">Скачать сводный шаблон</MockButton> — появляется при
              2+ действующих счетах: один Excel по всем, чтобы создать в УНФ один заказ покупателя
            </li>
            <li>
              «Удалить» — позиции счёта снова станут доступны для нового счёта; бейдж «✎ вручную»
              означает, что сумма счёта скорректирована правкой цен позиций
            </li>
            <li>
              <MockButton variant="secondary">Выгрузить ещё счёт (УНФ)</MockButton> в шапке заказа —
              добавляет следующий счёт по новым позициям в статусе «Счёт»
            </li>
          </ul>
          <p>
            Колонки Excel-шаблона: «Наименование», «Артикул», «Производитель», «Кол-во», «Ед.»,
            «Цена, ₽», «Категория», «Входит в группу». Наименование и артикул подставляются из
            справочника номенклатуры 1С, чтобы УНФ не создавала дубли номенклатуры.
          </p>
        </div>
      </MockCard>
      <MockCard>
        <div className="text-sm text-gray-600 dark:text-gray-400 space-y-3">
          <div className="font-medium text-gray-800 dark:text-gray-200">Блок «Оплаты»:</div>
          <ul className="list-disc list-inside space-y-1">
            <li>
              <MockButton>Зафиксировать оплату</MockButton> — поля «Сумма, ₽», «Назначение»
              (предоплата / доплата) и «Комментарий»
            </li>
            <li>
              Таблица платежей: «Дата», «Сумма», «Назначение», «Статус» (Запланирован / Получен /
              Отменён), «Комментарий»; действия «Править» и «Удалить»
            </li>
            <li>
              После каждого изменения платежей статус оплаты заказа пересчитывается автоматически
              («Частично оплачен» → «Оплачен»)
            </li>
          </ul>
          <p>
            Под сводкой заказа показывается строка плановой предоплаты: сколько нужно по проценту,
            сколько уже оплачено и сколько осталось до предоплаты. Для отсрочки и постоплаты —
            строка с количеством дней и ожидаемой суммой.
          </p>
          <MockWarning>
            Оплаты фиксируются вручную — авто-сверки с банком или УНФ пока нет. Не забывайте вносить
            платёж сразу после прихода денег.
          </MockWarning>
        </div>
      </MockCard>
    </div>
  )
}

export function OrdersDocumentsHistory() {
  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-2">
          Документы и история
        </h2>
        <p className="text-sm text-gray-600 dark:text-gray-400">
          В карточке заказа хранятся все файлы по сделке и полная история смен статуса — кто, когда
          и почему перевёл заказ.
        </p>
      </div>
      <MockTable
        caption="Типы документов заказа"
        columns={[{ header: 'Тип' }, { header: 'Что это' }]}
        rows={[
          ['Шаблон счёта (Excel)', 'Файл, который загружается в 1С:УНФ'],
          ['Гарантийное письмо', 'Письмо клиента при оплате без предоплаты'],
          ['Платёжное поручение', 'Подтверждение оплаты от клиента'],
          ['Счёт из УНФ (PDF)', 'Печатная форма счёта, сформированная в УНФ'],
          ['Прочее', 'Любые другие файлы по заказу'],
        ]}
      />
      <MockCard>
        <div className="text-sm text-gray-600 dark:text-gray-400 space-y-3">
          <div className="font-medium text-gray-800 dark:text-gray-200">Блок «Документы»:</div>
          <ul className="list-disc list-inside space-y-1">
            <li>Выберите тип документа в списке и прикрепите файл — он загрузится сразу</li>
            <li>Разрешены: jpg, png, webp, pdf, xlsx, doc, docx, ppt, pptx · до 50 МБ</li>
            <li>
              Клик по документу открывает предпросмотр: картинки и PDF показываются прямо в окне,
              Excel — первым листом таблицей; кнопки «Скачать» и «Удалить»
            </li>
          </ul>
          <div className="font-medium text-gray-800 dark:text-gray-200 mt-3">
            Блок «История статусов»:
          </div>
          <ul className="list-disc list-inside space-y-1">
            <li>Каждая запись: дата и время, переход «из → в», кто сменил (или «система»)</li>
            <li>
              К переходу сохраняется примечание — например, причина отказа или комментарий РОП при
              согласовании
            </li>
          </ul>
          <p className="mt-2">
            Комментарии и переписка по сделке ведутся не в заказе, а в карточке заявки — внизу
            карточки заказа есть ссылка на неё.
          </p>
        </div>
      </MockCard>
    </div>
  )
}

export function OrdersRolesApproval() {
  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-2">
          Роли и согласование РОП
        </h2>
        <p className="text-sm text-gray-600 dark:text-gray-400">
          Заказы — зона ответственности продаж. Закупка видит заказы только для чтения, а заказы с
          низкой предоплатой проходят согласование у руководителя отдела продаж.
        </p>
      </div>
      <MockTable
        caption="Кто что может в разделе «Заказы»"
        columns={[{ header: 'Роль' }, { header: 'Права' }]}
        rows={[
          ['Администратор', 'Все заказы: создание, правка, согласование, возврат отказа в работу'],
          ['Менеджер', 'Свои заказы (и заказы своих клиентов): создание счетов, оплаты, документы'],
          ['РОП', 'Как менеджер в своих городах + согласование заказов с предоплатой <50%'],
          ['Помощник', 'Заказы прикреплённых к нему менеджеров — как менеджер'],
          ['Закупщик / РОЗ', 'Только просмотр заказов менеджеров своих городов'],
          ['Генеральный директор', 'Только просмотр всех заказов'],
        ]}
      />
      <MockCard>
        <div className="text-sm text-gray-600 dark:text-gray-400 space-y-3">
          <div className="font-medium text-gray-800 dark:text-gray-200">
            Как работает согласование:
          </div>
          <ol className="list-decimal list-inside space-y-1">
            <li>
              Если плановая предоплата меньше 50% (в том числе гарантийное письмо, отсрочка,
              постоплата), заказ создаётся в статусе «Ждёт согласования РОП»
            </li>
            <li>Шаблон счёта при этом выгружается сразу — ждать согласования не нужно</li>
            <li>
              РОП или администратор нажимает{' '}
              <MockButton variant="secondary">Согласовать</MockButton> в карточке заказа
            </li>
            <li>
              В окне «Согласование заказа»: <MockButton>Одобрить</MockButton> → заказ переходит в
              «Подтверждён»; <MockButton variant="danger">Отклонить</MockButton> (обязательна
              причина) → «Отказ»
            </li>
          </ol>
          <p>
            Предоплата 50% и выше — согласование не требуется, заказ сразу становится «Подтверждён».
          </p>
          <MockWarning>
            Администратор может переопределить отказ (в том числе решение РОП): у отклонённого
            заказа появляется кнопка «Вернуть в работу» — одобрение вернёт заказ в «Подтверждён», а
            причина отказа будет очищена.
          </MockWarning>
          <p>
            Удалить заказ могут администратор, менеджер, РОП и помощник. При удалении счета,
            платежи, документы и история заказа удаляются безвозвратно, а заявка и её статусы не
            меняются.
          </p>
        </div>
      </MockCard>
    </div>
  )
}
